import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { useLiffProfile } from "../../context/LiffProfileContext";
import { useTranslation } from "react-i18next";

export default function ScanIdCard({ show, onClose, onOcr, side = "front" }) {
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const previewImageRef = useRef(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [loading, setLoading] = useState(false);

  const { authToken, userId } = useLiffProfile();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (show) startStreaming();
    return () => stopStreaming();
  }, [show]);

  const startStreaming = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      if (streamRef.current) {
        streamRef.current.srcObject = mediaStream;
        streamRef.current.onloadedmetadata = () => {
          streamRef.current.play().catch((err) => console.error("Play error:", err));
        };
        setCameraStream(mediaStream);
      }
    } catch (err) {
      console.error("Unable to access camera:", err);
    }
  };

  const stopStreaming = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
  };

  const handleCapture = async () => {
    const video = streamRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      const ctx = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const base64Image = canvas.toDataURL("image/png");

      previewImageRef.current.src = base64Image;
      stopStreaming();
      await sendOcrRequest(base64Image);
    }
  };

  const sendOcrRequest = async (base64Image) => {
    setLoading(true);
    try {
      const clientAuth = process.env.REACT_APP_CLIENT_AUTH;
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/ocr/id-card`;
      const blob = dataURLtoBlob(base64Image);
      const file = new File([blob], "idcard.png", { type: "image/png" });
      const formData = new FormData();
      formData.append("file", file);

      const headers = {
        Authorization: `Bearer ${authToken}`,
        "X-Client-Auth": clientAuth,
        "x-user-id": userId,
        "Accept-Language": i18n.language.toUpperCase(),
      };

      const response = await axios.post(apiUrl, formData, { headers });
      if (response.data?.statusCode === 200) {
        // console.log(`✅ OCR result (${side}):`, response.data);
        if (onOcr) onOcr(response.data.data, side);
        onClose();
      } else {
        console.warn("⚠️ OCR API returned unexpected status:", response.data);
      }
    } catch (err) {
      console.error("❌ OCR API error:", err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const dataURLtoBlob = (dataurl) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[999] bg-black flex flex-col items-center justify-between py-8">
      <div className="text-center" />
      <div
        className="relative border-4 border-white"
        style={{ width: "80vw", maxWidth: "400px", aspectRatio: "1.58", overflow: "hidden", borderRadius: "8px" }}
      >
        <video ref={streamRef} autoPlay playsInline muted className="absolute top-0 left-0 w-full h-full object-cover" />
      </div>
      <div className="flex flex-col items-center mt-4">
        <p className="text-white text-sm text-center mb-4">
          {side === "front" ? t("scan_front_id_card") : t("scan_back_id_card")}
        </p>
        <button
          onClick={handleCapture}
          className="w-20 h-20 bg-white rounded-full border-4 border-gray-400 flex items-center justify-center"
        >
          {loading ? (
            <svg
              className="animate-spin h-8 w-8 text-gray-700"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : null}
        </button>
      </div>
      <canvas ref={canvasRef} className="hidden" />
      <img ref={previewImageRef} className="hidden" />
    </div>
  );
}
